import { useState, useRef, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import {
  authService,
  extractDevOtpFromLoginPayload,
  extractOtpBypassFromLoginPayload,
} from '@/services'
import { getFingerprint, getIamAccessToken } from '@/api'
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Radio,
  GitBranch,
  Shield,
  Loader2,
  ArrowLeft,
  ShieldCheck,
  Tag,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import logo from '@/assets/logo.png'
import { cn } from '@/utils/cn'
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher'
import { APP_NAME, APP_SUBTITLE, LEAP_RED, LEAP_RED_DARK } from '@/constants/brand'

function looksLikePhoneId(raw: string): boolean {
  const t = raw.trim()
  if (!t || t.includes('@')) return false
  if (!/^\+?[\d\s().-]{7,}$/.test(t)) return false
  const digits = t.replace(/\D/g, '')
  return digits.length >= 7 && digits.length <= 15
}

/** Matches @leap-india/auth-ui: email, username / employee code, or phone. */
const loginSchema = z
  .object({
    identifier: z.string().min(1, 'Email, username, or employee code is required'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    rememberMe: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    const id = v.identifier.trim()
    if (!id) return
    if (id.includes('@')) {
      if (!z.string().email().safeParse(id).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['identifier'],
          message: 'Please enter a valid email address',
        })
      }
      return
    }
    if (looksLikePhoneId(id)) {
      const digits = id.replace(/\D/g, '')
      if (digits.length < 7 || digits.length > 15) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['identifier'],
          message: 'Please enter a valid phone number',
        })
      }
      return
    }
    if (id.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['identifier'],
        message: 'Username or employee code must be at least 2 characters',
      })
    }
  })

type LoginForm = z.infer<typeof loginSchema>

const BRAND = LEAP_RED
const BRAND_DARK = LEAP_RED_DARK
const OTP_LENGTH = 6

function OTPScreen({
  identifier,
  successMsg,
  serverError,
  onBack,
  onVerify,
  isLoading,
  devPrefillOtp,
  otpBypassAutofill,
}: {
  identifier: string
  successMsg?: string | null
  serverError?: string | null
  onBack: () => void
  onVerify: (otp: string) => void
  isLoading: boolean
  devPrefillOtp?: string | null
  otpBypassAutofill?: boolean
}) {
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const otpRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    setTimeout(() => otpRefs.current[0]?.focus(), 100)
  }, [])

  useEffect(() => {
    if (!devPrefillOtp || devPrefillOtp.length !== OTP_LENGTH) return
    const digits = devPrefillOtp.replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (digits.length !== OTP_LENGTH) return
    setOtp(digits.split(''))
    setTimeout(() => otpRefs.current[OTP_LENGTH - 1]?.focus(), 80)
  }, [devPrefillOtp])

  useEffect(() => {
    if (!serverError) return
    setOtp(Array(OTP_LENGTH).fill(''))
    setTimeout(() => otpRefs.current[0]?.focus(), 50)
  }, [serverError])

  const handleOtpChange = (index: number, value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(-1)
    const updated = [...otp]
    updated[index] = cleaned
    setOtp(updated)
    if (cleaned && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        otpRefs.current[index - 1]?.focus()
        const updated = [...otp]
        updated[index - 1] = ''
        setOtp(updated)
      } else {
        const updated = [...otp]
        updated[index] = ''
        setOtp(updated)
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    const updated = Array(OTP_LENGTH).fill('')
    pasted.split('').forEach((ch, i) => {
      updated[i] = ch
    })
    setOtp(updated)
    otpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus()
  }

  return (
    <motion.div
      key="otp"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
      className="w-full space-y-6"
    >
      <button
        type="button"
        onClick={() => {
          setOtp(Array(OTP_LENGTH).fill(''))
          onBack()
        }}
        disabled={isLoading}
        className="mb-2 flex items-center gap-2 text-sm text-[var(--muted-foreground)] transition-colors hover:text-[var(--foreground)]"
      >
        <ArrowLeft className="h-4 w-4" /> Back to login
      </button>

      <div className="flex flex-col items-center space-y-3 text-center">
        <div
          className="mb-2 flex h-16 w-16 items-center justify-center rounded-full border-2"
          style={{ background: '#FDECEA', borderColor: BRAND }}
        >
          <ShieldCheck className="h-8 w-8" style={{ color: BRAND }} />
        </div>
        <h2 className="text-2xl font-bold text-[var(--foreground)]">Two-Step Verification</h2>
        <p className="text-sm text-[var(--muted-foreground)]">
          We sent a 6-digit code to your registered email for
          <br />
          <b className="text-[var(--foreground)]">{identifier || 'your account'}</b>
        </p>
        {(otpBypassAutofill || (import.meta.env.DEV && devPrefillOtp)) && devPrefillOtp ? (
          <p className="max-w-sm rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {otpBypassAutofill
              ? 'OTP from IAM (otp_bypass) was filled automatically. Remove otp_bypass from IAM responses in production.'
              : 'Dev: OTP from login response is pre-filled. Disable with '}
            {!otpBypassAutofill ? <code className="text-[11px]">VITE_DEV_AUTO_OTP=false</code> : null}
          </p>
        ) : null}
      </div>

      {successMsg ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {successMsg}
        </div>
      ) : null}

      {serverError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center text-sm text-red-600">
          {serverError}
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          const otpValue = otp.join('')
          if (otpValue.length === OTP_LENGTH) onVerify(otpValue)
        }}
        className="space-y-6"
      >
        <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
          {otp.map((digit, index) => (
            <motion.input
              key={index}
              ref={(el) => {
                otpRefs.current[index] = el
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(index, e)}
              whileFocus={{ scale: 1.05 }}
              disabled={isLoading}
              className={cn(
                'h-14 w-11 rounded-lg border-2 bg-[var(--muted)] text-center text-xl font-bold transition-all',
                'focus:border-[#af0000] focus:outline-none focus:ring-2 focus:ring-[#af0000]',
                'disabled:cursor-not-allowed disabled:opacity-50',
                serverError
                  ? 'border-red-500 bg-red-50 text-red-600'
                  : digit
                    ? 'border-[#af0000] text-[#af0000]'
                    : 'border-[var(--border)] text-[var(--foreground)]'
              )}
            />
          ))}
        </div>

        <button
          type="submit"
          disabled={isLoading || otp.join('').length !== OTP_LENGTH}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#af0000] py-3 font-semibold text-white shadow-md transition-colors hover:bg-[#8B0000] disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Shield className="h-5 w-5" />}
          {isLoading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </motion.div>
  )
}

function BrandPanel() {
  const features = [
    {
      icon: Tag,
      title: 'Live control tower',
      desc: 'Monitor tag position, movements today, and warehouse vs customer stock from one overview.',
    },
    {
      icon: GitBranch,
      title: 'Journeys & parties',
      desc: 'Drill into a tag, document, or sender/receiver to see every related hop and docket.',
    },
    {
      icon: Radio,
      title: 'Reports & life cycle',
      desc: 'Export RFID reports and visualise asset life-cycle paths across C1 / C2 partners.',
    },
  ]

  return (
    <div
      className="relative hidden flex-col justify-center overflow-hidden px-20 text-white lg:flex lg:w-1/2"
      style={{ background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)` }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-10">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="rfidLoginDotGrid" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.5" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#rfidLoginDotGrid)" />
        </svg>
      </div>

      <div className="relative z-10 w-full max-w-lg">
        <div className="brand-entrance mb-12 flex items-center gap-4">
          <div className="h-16 w-auto">
            <img src={logo} alt="LEAP India" className="h-full object-contain brightness-0 invert" />
          </div>
          <div className="h-10 w-px bg-white/20" />
          <div>
            <p className="text-2xl font-extrabold leading-none tracking-tighter">{APP_NAME}</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">
              {APP_SUBTITLE}
            </p>
          </div>
        </div>

        <h1 className="brand-entrance mb-8 text-6xl font-black leading-[1.05] tracking-tight">
          See every tag. <br />
          <span className="text-white/60">Command every move.</span>
        </h1>

        <div className="mt-16 space-y-8">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="brand-entrance flex items-start gap-5"
              style={{ animationDelay: `${0.2 + i * 0.1}s` }}
            >
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/15 backdrop-blur-md">
                <f.icon size={22} className="text-white" />
              </div>
              <div>
                <h3 className="mb-1 text-lg font-bold">{f.title}</h3>
                <p className="text-sm leading-relaxed text-white/70">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, resetAuth, setLoading, setError, isLoading, error } = useAuthStore()
  const [showPassword, setShowPassword] = useState(false)
  const [screen, setScreen] = useState<'login' | 'otp'>('login')
  const [submittedIdentifier, setSubmittedIdentifier] = useState('')
  const [otpSuccessMsg, setOtpSuccessMsg] = useState<string | null>(null)
  const [devPrefillOtp, setDevPrefillOtp] = useState<string | null>(null)
  const [otpBypassAutofill, setOtpBypassAutofill] = useState(false)

  useEffect(() => {
    const fp = getFingerprint()
    const jwt = getIamAccessToken()
    if (useAuthStore.getState().isAuthenticated && (!fp || !jwt)) {
      resetAuth()
    }
  }, [resetAuth])

  const devAutoOtp =
    import.meta.env.VITE_DEV_AUTO_OTP === 'true' ||
    (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTO_OTP !== 'false')

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { rememberMe: false },
  })

  const onSubmit = async (data: LoginForm) => {
    try {
      resetAuth()
      setLoading(true)
      setError(null)

      const identifier = data.identifier.trim()
      const loginPayload = await authService.login(identifier, data.password)

      setSubmittedIdentifier(identifier)
      const bypass = extractOtpBypassFromLoginPayload(loginPayload)
      setOtpBypassAutofill(Boolean(bypass))
      const fromApiTree = extractDevOtpFromLoginPayload(loginPayload)
      const mockRaw = import.meta.env.VITE_DEV_MOCK_OTP
      const fromMock = typeof mockRaw === 'string' && /^\d{6}$/.test(mockRaw) ? mockRaw : null
      const prefill = bypass ?? (devAutoOtp ? (fromApiTree ?? fromMock) : null)
      setDevPrefillOtp(prefill)
      setOtpSuccessMsg("We've sent a 6-digit code to your registered email.")
      setError(null)
      setScreen('otp')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleOTPVerify = useCallback(
    async (code: string) => {
      try {
        setLoading(true)
        setError(null)

        if (code.length !== 6) {
          setError('Invalid code')
          return
        }

        await authService.verifyOtp(code)
        const me = await authService.getCurrentUser()
        if (!me.success || !me.data) {
          setError('Failed to load user profile.')
          return
        }

        login(me.data)
        setOtpSuccessMsg(null)
        navigate('/dashboard', { replace: true })
      } catch (err: unknown) {
        const msg =
          err instanceof Error
            ? err.message
            : err &&
                typeof err === 'object' &&
                'message' in err &&
                typeof (err as { message?: unknown }).message === 'string'
              ? String((err as { message: string }).message)
              : 'Sign-in failed. Check the OTP and try again.'
        setError(msg || 'Sign-in failed. Check the OTP and try again.')
      } finally {
        setLoading(false)
      }
    },
    [login, navigate, setLoading, setError]
  )

  const inputBase =
    'w-full pl-11 pr-4 py-3 rounded-xl border-2 text-sm font-medium outline-none transition-all duration-200 bg-[var(--muted)] text-[var(--foreground)] placeholder:text-[var(--muted-foreground)]'
  const inputStyle = (hasError: boolean) => ({
    borderColor: hasError ? BRAND : '#E0E0E0',
    boxShadow: hasError ? `0 0 0 3px rgba(175,0,0,0.12)` : 'none',
  })
  const inputFocusStyle = { borderColor: BRAND, boxShadow: `0 0 0 3px rgba(175,0,0,0.12)` }

  return (
    <>
      <style>{`
        @keyframes brandEntrance {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .brand-entrance {
          opacity: 0;
          animation: brandEntrance 0.55s cubic-bezier(0.22,1,0.36,1) forwards;
        }
        @keyframes formEntrance {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .form-entrance {
          animation: formEntrance 0.5s cubic-bezier(0.22,1,0.36,1) forwards;
        }
      `}</style>

      <div className="relative flex min-h-screen bg-[var(--background)] text-[var(--foreground)] transition-colors">
        <BrandPanel />

        <div className="relative flex flex-1 items-center justify-center px-4 py-12 sm:px-8">
          <div className="absolute right-4 top-4 z-20 sm:right-8 sm:top-6">
            <ThemeSwitcher variant="surface" className="min-w-[160px] shadow-sm" />
          </div>
          <div className="w-full max-w-lg">
            <div className="form-entrance mb-8 flex flex-col items-center lg:hidden">
              <div className="mb-4 h-16 w-auto">
                <img src={logo} alt="LEAP India" className="h-full object-contain" />
              </div>
              <p className="text-lg font-extrabold leading-none text-[var(--foreground)]">{APP_NAME}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--muted-foreground)]">
                {APP_SUBTITLE}
              </p>
            </div>

            <div
              className="form-entrance rounded-2xl border border-[var(--border)] bg-[var(--card)] p-10 shadow-xl"
              style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.08)' }}
            >
              <AnimatePresence mode="wait">
                {screen === 'login' ? (
                  <motion.div
                    key="login"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="mb-8">
                      <h2 className="text-3xl font-bold text-[var(--foreground)]">Welcome back</h2>
                      <p className="mt-1.5 text-sm text-[var(--muted-foreground)]">
                        Sign in with your LEAP IAM email, username, or employee code
                      </p>
                    </div>

                    {error ? (
                      <div
                        className="mb-5 flex items-start gap-2.5 rounded-xl px-4 py-3 text-sm"
                        style={{ backgroundColor: '#FDECEA', border: '1px solid #F1948A' }}
                      >
                        <span className="mt-0.5">❌</span>
                        <p className="text-red-800">{error}</p>
                      </div>
                    ) : null}

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                      <div>
                        <label className="mb-1.5 block text-sm font-semibold text-[var(--foreground)]">
                          Email, username, or employee code
                        </label>
                        <div className="relative">
                          <Mail
                            size={16}
                            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2"
                            style={{ color: errors.identifier ? BRAND : '#9CA3AF' }}
                          />
                          <input
                            {...register('identifier')}
                            type="text"
                            autoComplete="username"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            placeholder="you@company.com or LIPL0928"
                            disabled={isLoading}
                            className={inputBase}
                            style={
                              errors.identifier
                                ? { ...inputStyle(true), borderColor: BRAND }
                                : inputStyle(false)
                            }
                            onFocus={(e) => {
                              if (!errors.identifier) Object.assign(e.target.style, inputFocusStyle)
                            }}
                            onBlur={(e) => {
                              if (!errors.identifier)
                                Object.assign(e.target.style, {
                                  borderColor: '#E0E0E0',
                                  boxShadow: 'none',
                                })
                            }}
                          />
                        </div>
                        {errors.identifier ? (
                          <p className="mt-1.5 text-xs font-medium" style={{ color: BRAND }}>
                            {errors.identifier.message}
                          </p>
                        ) : null}
                      </div>

                      <div>
                        <label className="mb-1.5 block text-sm font-semibold text-[var(--foreground)]">
                          Password
                        </label>
                        <div className="relative">
                          <Lock
                            size={16}
                            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2"
                            style={{ color: errors.password ? BRAND : '#9CA3AF' }}
                          />
                          <input
                            {...register('password')}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            placeholder="••••••••"
                            disabled={isLoading}
                            className={`${inputBase} pr-11`}
                            style={
                              errors.password
                                ? { ...inputStyle(true), borderColor: BRAND }
                                : inputStyle(false)
                            }
                            onFocus={(e) => {
                              if (!errors.password) Object.assign(e.target.style, inputFocusStyle)
                            }}
                            onBlur={(e) => {
                              if (!errors.password)
                                Object.assign(e.target.style, {
                                  borderColor: '#E0E0E0',
                                  boxShadow: 'none',
                                })
                            }}
                          />
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 rounded p-0.5 transition-colors hover:opacity-60"
                            style={{ color: '#9CA3AF' }}
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        {errors.password ? (
                          <p className="mt-1.5 text-xs font-medium" style={{ color: BRAND }}>
                            {errors.password.message}
                          </p>
                        ) : null}
                      </div>

                      <div className="flex items-center justify-between">
                        <label className="flex cursor-pointer select-none items-center gap-2">
                          <input
                            {...register('rememberMe')}
                            type="checkbox"
                            className="h-4 w-4 rounded"
                            style={{ accentColor: BRAND }}
                          />
                          <span className="text-sm text-gray-600">Remember me</span>
                        </label>
                        <button
                          type="button"
                          className="text-sm font-semibold transition-opacity hover:opacity-70"
                          style={{ color: BRAND }}
                        >
                          Forgot password?
                        </button>
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="mt-2 flex w-full items-center justify-center gap-2.5 rounded-xl py-4 text-base font-semibold text-white transition-all duration-200"
                        style={{
                          background: isLoading
                            ? '#ccc'
                            : `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
                          boxShadow: isLoading ? 'none' : '0 8px 30px rgba(175,0,0,0.3)',
                          cursor: isLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Signing in…
                          </>
                        ) : (
                          <>
                            <Lock size={16} />
                            Sign in securely
                          </>
                        )}
                      </button>
                    </form>

                    <div className="relative my-7">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-[var(--border)]" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-[var(--card)] px-4 text-xs font-medium text-[var(--muted-foreground)]">
                          LEAP · {APP_NAME}
                        </span>
                      </div>
                    </div>

                    <p className="text-center text-xs text-[var(--muted-foreground)]">
                      By signing in you agree to our{' '}
                      <button className="underline hover:opacity-70" style={{ color: BRAND }}>
                        Terms of Service
                      </button>{' '}
                      and{' '}
                      <button className="underline hover:opacity-70" style={{ color: BRAND }}>
                        Privacy Policy
                      </button>
                      .
                    </p>
                  </motion.div>
                ) : (
                  <OTPScreen
                    key={`otp-${submittedIdentifier}-${devPrefillOtp ?? ''}`}
                    identifier={submittedIdentifier}
                    successMsg={otpSuccessMsg}
                    serverError={error}
                    onBack={() => {
                      setScreen('login')
                      setError(null)
                      setOtpSuccessMsg(null)
                      setDevPrefillOtp(null)
                      setOtpBypassAutofill(false)
                    }}
                    onVerify={handleOTPVerify}
                    isLoading={isLoading}
                    devPrefillOtp={devPrefillOtp}
                    otpBypassAutofill={otpBypassAutofill}
                  />
                )}
              </AnimatePresence>
            </div>

            <p className="mt-6 text-center text-xs text-[var(--muted-foreground)]">
              © {new Date().getFullYear()} LEAP India — {APP_NAME}
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
